import { useBaseTranslation } from 'articleEditor/hooks/translations/useBaseTranslation';
import { useDefaultTranslation } from 'articleEditor/hooks/useDefaultTranslation';
import { RetranslationWarningModal } from 'articleEditor/modals/retranslationWarningModal';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { MyRootBlock } from 'lib-knowledge-document-structure/plateTypes';
import { useModal } from 'lib-knowledge-modal';
import { TranslatingType } from 'lib-knowledge-remote-types/general.types';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

export const useTranslateBlock = () => {
  const defaultTranslation = useDefaultTranslation();
  const editor = useMyEditorRef();
  const { errorNotify } = useNotification();
  const { showModal } = useModal();
  const replaceBlock = useCallback(
    (block: MyRootBlock) => {
      const blockLocation = editor.children.findIndex(
        (child) => child.id === block.id,
      );
      if (blockLocation !== -1) {
        editor.removeNodes({ at: [blockLocation] });
        editor.insertNodes(block, { at: [blockLocation] });
      }
    },
    [editor],
  );

  const { translate, stopLoadingAndShowErrorNotification, setGlobalLoading } =
    useBaseTranslation({
      translatingType: TranslatingType.BLOCK,
      onParsedBlock: replaceBlock,
    });

  const getBlockInDefaultTranslation = (
    block: MyRootBlock,
  ): MyRootBlock | undefined => {
    return defaultTranslation?.draft.content?.children.find(
      (child) => child.id === block.id,
    );
  };

  const translateBlock = async (block: MyRootBlock) => {
    if (!defaultTranslation?.draft.content?.children) {
      stopLoadingAndShowErrorNotification();
      return;
    }
    setGlobalLoading(true, block.id);
    try {
      const blockInDefaultTranslation = getBlockInDefaultTranslation(block);
      if (!blockInDefaultTranslation) {
        // remove `translatedAt` from the block that does not have an equivalent in the default translation
        replaceBlock({
          ...block,
          translatedAt: undefined,
        });
        errorNotify({
          title: 'Error',
          text: 'The selected block was not found in the default translation',
        });
        return;
      }
      await translate({ content: [blockInDefaultTranslation] });
    } catch {
      stopLoadingAndShowErrorNotification();
    }
  };

  return async (blockId: string): Promise<void> => {
    const block = editor.children.find(
      (child) => child.id === blockId,
    ) as MyRootBlock;
    // This should not happen
    if (!block) {
      errorNotify({
        title: 'Error',
        text: 'The selected block was not found',
      });
      return;
    }
    if (
      block.translatedAt &&
      block.updatedAt &&
      Date.parse(block.updatedAt) > Date.parse(block.translatedAt)
    ) {
      showModal(RetranslationWarningModal, {
        title: 'Overwrite block translation?',
        body: `This block has been modified after it was translated. Are you sure you want to retranslate it? This action will overwrite the block translation and is NOT reversible.`,
        onAccept: () => translateBlock(block),
      });
    } else {
      await translateBlock(block);
    }
  };
};
